import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { API_URL } from "../config";

interface BookingCar {
  id: number;
  booking_date: string;
  end_date:string;
  created_at: string;
  updated_at: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  address: string;
  car_id: number;
  new_price: string;
  duration: string;
  status: string;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  cancelBg: "#d3be76",
  cancelHover: "#c2ad65",
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 220,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 60,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 280,
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
      }}>
        {/* Filter Bar Skeleton */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-7 col-lg-6">
              <div style={{
                width: "100%",
                height: 42,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>

        {/* Table Header Skeleton */}
        <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div className="row">
            {["Customer", "Email", "Rental Date", "Amount", "Status", "Actions"].map((_, i) => (
              <div key={i} className="col" style={{ padding: "0 8px" }}>
                <div style={{
                  width: i === 0 ? 80 : i === 1 ? 100 : i === 2 ? 90 : i === 3 ? 60 : i === 4 ? 60 : 50,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Rows Skeleton */}
        {[...Array(8)].map((_, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              padding: "14px 16px",
              borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
              display: "flex",
              alignItems: "center"
            }}
          >
            {[...Array(6)].map((_, colIndex) => (
              <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                <div style={{
                  width: colIndex === 0 ? 120 : colIndex === 1 ? 160 : colIndex === 2 ? 100 : colIndex === 3 ? 60 : colIndex === 4 ? 80 : 70,
                  height: colIndex === 0 ? 16 : 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                {colIndex === 0 && (
                  <div style={{
                    width: 100,
                    height: 12,
                    marginTop: 4,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 3,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Pagination Skeleton */}
        <div style={{
          padding: "12px 20px",
          borderTop: "1px solid #edf2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{
            width: 200,
            height: 16,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
          <div style={{ display: "flex", gap: "6px" }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{
                width: i === 0 || i === 4 ? 70 : 32,
                height: 32,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ListRequestRentCar = () => {
  const [bookingCars, setBookingCars] = useState<BookingCar[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [fromItem, setFromItem] = useState(0);
  const [toItem, setToItem] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchBookings = (page = 1) => {
    setIsLoading(true);
    axios
      .get(`${API_URL}/booking-cars?page=${page}`, config_token)
      .then((res) => {
        setBookingCars(res.data.data || []);
        setCurrentPage(res.data.current_page || 1);
        setLastPage(res.data.last_page || 1);
        setTotalItems(res.data.total || 0);
        setFromItem(res.data.from || 0);
        setToItem(res.data.to || 0);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Error fetching car bookings");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const filteredBookingCars = useMemo(() => {
    if (!searchTerm) return bookingCars;
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return bookingCars.filter(
      (booking) =>
        booking.firstname.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.lastname.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.email.toLowerCase().includes(lowercasedSearchTerm) ||
        booking.status.toLowerCase().includes(lowercasedSearchTerm)
    );
  }, [bookingCars, searchTerm]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: "#fef3c7", text: "#92400e", label: "Pending" },
      confirmed: { bg: "#dcfce7", text: "#166534", label: "Confirmed" },
      cancelled: { bg: "#fee2e2", text: "#991b1b", label: "Cancelled" },
      completed: { bg: "#dbeafe", text: "#1e40af", label: "Completed" },
    };

    const config = statusConfig[status.toLowerCase()] || {
      bg: "#f3f4f6",
      text: "#374151",
      label: status,
    };

    return (
      <span
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: 600,
          backgroundColor: config.bg,
          color: config.text,
          textTransform: "capitalize",
        }}
      >
        {config.label}
      </span>
    );
  };

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;
    try {
      const bookingCarToDelete = filteredBookingCars[selectedRow];
      await axios.delete(`${API_URL}/booking-cars/${bookingCarToDelete.id}`, config_token);

      setBookingCars((prev) => prev.filter((b) => b.id !== bookingCarToDelete.id));
      fetchBookings(currentPage);
      toast.success("Car booking deleted successfully");
    } catch (error: any) {
      console.error("Error deleting car booking:", error.response || error.message);
      toast.error("Error deleting booking.");
    } finally {
      setShowModal(false);
      setSelectedRow(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/details-rent-car/${id}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    if (dateString.includes(" ")) return dateString.split(" ")[0];
    if (dateString.includes("T")) return dateString.split("T")[0];
    return dateString;
  };

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  if (isLoading && bookingCars.length === 0) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px 20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* SECTION EN-TÊTE */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h1
                  style={{
                    color: "#294b5a",
                    fontSize: "22px",
                    fontWeight: 700,
                    margin: 0,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Manage Car Rentals
                </h1>
                <span
                  style={{
                    backgroundColor: "#294b5a",
                    color: "#d3be76",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: "12px",
                    letterSpacing: "0.5px",
                  }}
                >
                  System
                </span>
              </div>
              <p
                style={{
                  color: "#64748b",
                  fontSize: "13.5px",
                  margin: 0,
                  fontWeight: 400,
                }}
              >
                Overview and management of total{" "}
                <strong style={{ color: "#294b5a" }}>{totalItems}</strong> car rentals
                {searchTerm && ` • ${filteredBookingCars.length} filtered`}
              </p>
            </div>
          </div>

          {/* BLOC PRINCIPAL */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
              overflow: "hidden",
              marginTop: "15px",
            }}
          >
            {/* BARRE DE FILTRES */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #edf2f7",
                backgroundColor: "#ffffff",
              }}
            >
              <div className="row g-3 align-items-center">
                <div className="col-12 col-md-7 col-lg-6">
                  <div className="position-relative">
                    <input
                      type="text"
                      placeholder="Search rentals by customer name, email, status..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{
                        width: "100%",
                        padding: "9px 36px 9px 38px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "8px",
                        fontSize: "13px",
                        outline: "none",
                        color: "#0f172a",
                        backgroundColor: "#f8fafc",
                        transition: "all 0.2s ease",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#294b5a";
                        e.target.style.backgroundColor = "#ffffff";
                        e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#cbd5e1";
                        e.target.style.backgroundColor = "#f8fafc";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <i
                      className="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        left: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "13px",
                        color: "#94a3b8",
                      }}
                    ></i>
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={clearSearch}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#94a3b8",
                          padding: "0",
                        }}
                      >
                        <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-3 col-lg-2">
                  <button
                    type="button"
                    onClick={() => fetchBookings(currentPage)}
                    style={{
                      width: "100%",
                      padding: "9px 16px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#ffffff",
                      color: "#294b5a",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.15s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                      e.currentTarget.style.borderColor = "#294b5a";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                      e.currentTarget.style.borderColor = "#cbd5e1";
                    }}
                  >
                    <i className="fa-solid fa-rotate" style={{ fontSize: "12px" }}></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* TABLEAU */}
            {filteredBookingCars.length === 0 ? (
              <div style={{ padding: "48px 20px", textAlign: "center" }}>
                <i className="fa-solid fa-car" style={{ fontSize: "24px", color: "#cbd5e1", display: "block", marginBottom: "8px" }}></i>
                <div style={{ fontSize: "13.5px", color: "#64748b" }}>
                  {searchTerm ? "No car rentals found matching your search." : "No car rentals found."}
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#294b5a",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      textDecoration: "underline",
                      marginTop: "8px",
                    }}
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="table-responsive" style={{ margin: 0 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Customer</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Email</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Rental Date</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Period</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Amount</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px" }}>Status</th>
                        <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, letterSpacing: "0.5px", textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookingCars.map((item, index) => (
                        <tr
                          key={item.id}
                          style={{ borderBottom: "1px solid #f1f5f9", transition: "background-color 0.15s ease" }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                              {item.firstname} {item.lastname}
                            </div>
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#64748b" }}>
                            {item.email}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            <strong>From</strong> {formatDate(item.booking_date)} <strong>To</strong>  {formatDate(item.end_date)}
                          </td>

                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
 
                          </td>
                          
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 600, color: "#294b5a" }}>
                            OMR {item.new_price}
                          </td>
                          <td style={{ padding: "14px 16px" }}>
                            {getStatusBadge(item.status)}
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "right" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              <button
                                type="button"
                                onClick={() => handleEditClick(item.id)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#294b5a",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f8fafc";
                                  e.currentTarget.style.borderColor = "#cbd5e1";
                                  e.currentTarget.style.color = "#d3be76";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#294b5a";
                                }}
                                title="View Details"
                              >
                                <i className="fa-solid fa-eye" style={{ fontSize: "13px" }}></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(index)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#64748b",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fef2f2";
                                  e.currentTarget.style.borderColor = "#fecaca";
                                  e.currentTarget.style.color = "#dc2626";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#64748b";
                                }}
                                title="Delete"
                              >
                                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}
                {totalItems > 0 && lastPage > 1 && (
                  <div
                    style={{
                      padding: "12px 20px",
                      borderTop: "1px solid #edf2f7",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    <div style={{ fontSize: "13px", color: "#64748b" }}>
                      Showing <strong>{fromItem}</strong> to <strong>{toItem}</strong> of <strong>{totalItems}</strong> entries
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        onClick={() => fetchBookings(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12.5px",
                          fontWeight: "600",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          backgroundColor: "#ffffff",
                          color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        Previous
                      </button>

                      {getPaginationRange(currentPage, lastPage).map((item, index) => {
                        if (item === "...") {
                          return (
                            <span
                              key={`dots-${index}`}
                              style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: "600", userSelect: "none" }}
                            >
                              ...
                            </span>
                          );
                        }
                        const pageNum = item as number;
                        const isActive = currentPage === pageNum;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => fetchBookings(pageNum)}
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "12.5px",
                              fontWeight: "600",
                              borderRadius: "6px",
                              border: isActive ? "none" : "1px solid #cbd5e1",
                              backgroundColor: isActive ? "#294b5a" : "#ffffff",
                              color: isActive ? "#ffffff" : "#334155",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => fetchBookings(currentPage + 1)}
                        disabled={currentPage === lastPage}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12.5px",
                          fontWeight: "600",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          backgroundColor: "#ffffff",
                          color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                          cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Confirmation de suppression */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>

              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0", letterSpacing: "-0.01em" }}>
                Delete Car Rental?
              </h2>

              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
                Are you sure you want to delete this car rental booking? This action cannot be undone.
              </p>

              {selectedRow !== null && filteredBookingCars[selectedRow] && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>
                    {filteredBookingCars[selectedRow].firstname} {filteredBookingCars[selectedRow].lastname}
                  </div>
                  <div style={{ fontSize: "12.5px", color: "#64748b" }}>
                    {filteredBookingCars[selectedRow].email} · ${filteredBookingCars[selectedRow].new_price}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff" }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListRequestRentCar;